import { useGetRelateProduct } from '@/hooks/Product/use-get-relate-product';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { formatString } from '@/lib/format-string';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import Price from '../Price';
import { Carousel, CarouselContent, CarouselItem } from '../ui/carousel';

const RelatedProducts = props => {
  const { className, urlKey } = props;

  const { relatedProducts } = useGetRelateProduct({
    originalUrlKey: urlKey,
  });

  const { isMobile } = useIsMobile();

  if (relatedProducts?.length === 0) {
    return null;
  }

  return (
    <div className={cn('mx-auto mt-5 max-w-site lg_mt-[60px]', className)}>
      <h2 className='px-3 font-teko-medium text-[30px] leading-none lg_px-0 lg_text-[40px]'>YOU MAY ALSO LIKE</h2>

      <div className='mt-5 lg_grid lg_grid-cols-4 lg_gap-x-[27px]'>
        {isMobile ? (
          <MobileCarousel relatedProducts={relatedProducts} />
        ) : (
          <>
            {relatedProducts?.map(x => {
              const smallImage = x.small_image?.url;
              const hoverImage = x.hover_image;
              const href = `/${x.url_key}.html`;
              const finalPriceValue = x.price_range?.minimum_price?.final_price?.value;
              const currency = x.price_range?.minimum_price?.final_price?.currency;

              return (
                <div key={x.id}>
                  <ProductCard
                    href={href}
                    smallImage={smallImage}
                    hoverImage={hoverImage}
                    name={x.name}
                    serialName={x.serial_name}
                    currency={currency}
                    finalPriceValue={finalPriceValue}
                  />
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

const MobileCarousel = props => {
  const { relatedProducts } = props;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [api, setApi] = useState(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = emblaApi => {
      if (!emblaApi) return;

      setSelectedIndex(emblaApi.selectedScrollSnap());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    onSelect(api);
    api.on('reInit', onSelect).on('select', onSelect);
  }, [api]);

  return (
    <>
      <Carousel
        opts={{ align: 'start' }}
        setApi={setApi}
        className='pl-3 lg_pl-0'
      >
        <CarouselContent className='-ml-5'>
          {relatedProducts?.map((x, i, arr) => {
            const smallImage = x.small_image?.url;
            const hoverImage = x.hover_image;
            const href = `/${x.url_key}.html`;
            const finalPriceValue = x.price_range?.minimum_price?.final_price?.value;
            const currency = x.price_range?.minimum_price?.final_price?.currency;
            const isLastOne = i === arr.length - 1;

            return (
              <CarouselItem
                key={x.id}
                className={clsx(isLastOne ? 'basis-[342px] pl-5 pr-3' : 'basis-[330px] pl-5')}
              >
                <ProductCard
                  href={href}
                  smallImage={smallImage}
                  hoverImage={hoverImage}
                  name={x.name}
                  serialName={x.serial_name}
                  currency={currency}
                  finalPriceValue={finalPriceValue}
                />
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      {(canScrollPrev || canScrollNext) && relatedProducts?.length > 1 ? (
        <div className='px-3'>
          <div className='relative mt-5 h-[1px] bg-e0e0e0  '>
            <div
              className='absolute left-0 top-1/2 h-[3px] -translate-y-1/2 bg-black transition-[width] duration-2xl ease-linear'
              style={{
                width: `${Math.min(100, Math.floor(((selectedIndex + 1) / relatedProducts?.length) * 100))}%`,
              }}
            ></div>
          </div>
        </div>
      ) : null}
    </>
  );
};

const ProductCard = props => {
  const { href, smallImage, hoverImage, name, serialName, currency, finalPriceValue } = props;
  return (
    <>
      <Link
        className='block'
        href={href}
      >
        <div className='group relative bg-f9f9f9'>
          <Image
            src={smallImage}
            width={1265}
            height={1600}
            alt={name}
            loader={({ src }) => src}
            className='h-auto w-full'
          />
          {hoverImage ? (
            <Image
              src={hoverImage}
              width={1265}
              height={1600}
              alt={name}
              loader={({ src }) => src}
              className='absolute left-0 top-0 z-[1] h-auto w-full opacity-0 transition-opacity duration-2xl ease-linear lg_group-hover_opacity-100 '
            />
          ) : null}
        </div>
      </Link>
      <div className='mt-2 lg_mt-2.5'>
        {serialName ? (
          <h3 className=' font-teko-medium text-[22px] uppercase leading-8  lg_leading-[37px]'>{serialName}</h3>
        ) : null}
        <Link
          className=' block max-w-max font-roboto-light text-[16px] leading-5 lg_font-roboto-regular lg_text-lg lg_leading-[30px]'
          href={href}
        >
          <h3>{formatString(name)}</h3>
        </Link>
      </div>

      <div className='mt-4 font-roboto-medium text-[18px] lg_mt-5 lg_text-xl'>
        <Price
          currencyCode={currency}
          value={finalPriceValue}
        />
      </div>
    </>
  );
};

export default RelatedProducts;
